/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IconButton } from '@fluentui/react/lib/Button';
import { ICalloutContentStyleProps, ICalloutContentStyles } from '@fluentui/react/lib/Callout';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { DropdownMenuItemType, IDropdownOption, IDropdownStyleProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { IIconProps, IIconStyles, Icon } from '@fluentui/react/lib/Icon';
import { Label } from '@fluentui/react/lib/Label';
import { ILinkStyleProps, ILinkStyles, Link } from '@fluentui/react/lib/Link';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { CollapsedErrorMessage } from 'src/core/components/CollapsedErrorMessage';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logDTButtonClick, logDTLinkClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { getDTActiveStepName } from 'src/core/utils/telemetryUtils';
import { TranslationCreationSteps } from 'src/translation/components/TranslationCreationPage';
import { TranslationToolFooter } from 'src/translation/components/TranslationToolFooter';
import { getTranslationPageName } from 'src/translation/utils/translationCreationUtils';

export type TranslationConfigurationPageProps = {
    onSubmit: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
    isSaveDisabled?: boolean;
    isReturningUser: boolean;
    saveChanges?: () => void;
    isResourcesLoading: boolean;
    storageAccountsError?: Error;
    selectedStorageAccount: string;
    isStorageAccountsLoading: boolean;
    activeStep: TranslationCreationSteps;
    onRefreshStorageAccounts: () => void;
    onNext: (storageAccount: string) => void;
    selectedResourceStore: AzureCsAccountStore;
    translatorResources: AzureCsAccountStore[];
    storageAccounts: AzureStorageAccountStore[];
    onResourceChange: (resourceId: string) => void;
    onStorageAccountChange: (storageAccount: string) => void;
};

// Component messages
const messages = defineMessages({
    pageTitle: {
        id: 'translation.translationConfigurationPage.pageTitle',
        defaultMessage: 'Set a one time configuration for your translation jobs'
    },
    pageSubtitle: {
        id: 'translation.translationConfigurationPage.pageSubtitle',
        defaultMessage:
            'This is a one time configuration to pick your Azure Translator resource and select the location of your documents for your translation jobs. Future jobs will follow this configuration by default. You can change the configuration at any time in'
    },
    pageSubtitleLink: {
        id: 'translation.translationConfigurationPage.pageSubtitleLink',
        defaultMessage: 'Translation settings.'
    },
    translatorResourceTitle: {
        id: 'translation.translationConfigurationPage.translatorResourceTitle',
        defaultMessage: 'Azure Translator resource'
    },
    translatorResourceSubtitle: {
        id: 'translation.translationConfigurationPage.translatorResourceSubtitle',
        defaultMessage: 'Connect or create the Azure Translator resource that will be billed for your translation jobs.'
    },
    selectTranslatorResource: {
        id: 'translation.translationConfigurationPage.selectTranslatorResource',
        defaultMessage: 'Select your Translator resource'
    },
    tierNote: {
        id: 'translation.translationConfigurationPage.tierNote',
        defaultMessage: '(Standard tiers (S1 or D3) only allowed)'
    },
    selectTranslatorResourcePlaceholder: {
        id: 'translation.translationConfigurationPage.selectTranslatorResourcePlaceholder',
        defaultMessage: 'Select a Translator resource'
    },
    storageAccountSectionTitle: {
        id: 'translation.translationConfigurationPage.storageAccountSectionTitle',
        defaultMessage: 'Document storage'
    },
    storageAccountSectionSubtitle: {
        id: 'translation.translationConfigurationPage.storageAccountSectionSubtitle',
        defaultMessage:
            'This is the Azure Blob storage which contains the documents you will translate. You must have "Storage Blob Data Contributor" roles on the storage account to proceed. Loading your storage accounts may take a while.'
    },
    selectStorageAccountTitle: {
        id: 'translation.translationConfigurationPage.selectStorageAccountTitle',
        defaultMessage: 'Select your Azure Blob storage account'
    },
    selectStorageAccountPlaceholder: {
        id: 'translation.translationConfigurationPage.selectStorageAccountPlaceholder',
        defaultMessage: 'Select a Azure Blob storage account'
    },
    createBlobStorageAccountLink: {
        id: 'translation.translationConfigurationPage.createBlobStorageAccountLink',
        defaultMessage: 'Don’t have a Blob storage account? Learn how to create one'
    },
    createNewResourceLink: {
        id: 'translation.translationConfigurationPage.createNewResourceLink',
        defaultMessage: 'Create a new Translator resource'
    },
    saveButton: {
        id: 'translation.translationConfigurationPage.saveButton',
        defaultMessage: 'Save'
    },
    enableIdentity: {
        id: 'translation.translationConfigurationPage.enableIdentity',
        defaultMessage: 'Enable managed identity'
    },
    invalidResource: {
        id: 'translation.translationConfigurationPage.invalidResource',
        defaultMessage: 'You must enable managed identity to connect a storage account.'
    },
    invalidPermissionsStorageAccount: {
        id: 'translation.translationConfigurationPage.invalidPermissionsStorageAccount',
        defaultMessage:
            'The user must have "Storage Blob Data Contributor" role in the Azure Blob storage account to run document translation jobs'
    },
    enableCORSFailed: {
        id: 'translation.translationConfigurationPage.enableCORSFailed',
        defaultMessage: `The portal cannot access your storage account "{storageAccountName}",
        please enable CORS policies on your storage account.`
    },
    emptyStorageAccount: {
        id: 'translation.translationConfigurationPage.emptyStorageAccount',
        defaultMessage: 'You do not have access to any storage account'
    },
    refreshBtnTitle: {
        id: 'translation.translationConfigurationPage.refreshBtnTitle',
        defaultMessage: 'Refresh storage accounts'
    }
});

const { styleComponent } = getStylistV2('TranslationConfigurationPage');
const theme = getScopedTheme('TranslationConfigurationPage');

const ContainerWrapper = styleComponent<IStackProps>(Stack)('ContainerWrapper', () => ({
    paddingLeft: '32px',
    paddingRight: '32px',
    overflow: 'auto',
    marginBottom: '10px'
}));

const dropdownStyles = classNamesFunction<IDropdownStyleProps, IDropdownStyles>()({ dropdown: { width: '300px' } });
const calloutStyle = classNamesFunction<ICalloutContentStyleProps, ICalloutContentStyles>()({
    calloutMain: { maxHeight: '300px !important' }
});
const SecondaryText = styleComponent<LuisTextProps>(LuisText)('SecondaryText', pageTheme => ({
    color: pageTheme.secondaryTextColor,
    marginTop: '5px'
}));
const createResourceButtonStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({
    root: { fontSize: '14px' }
});
const addIconStyles = classNamesFunction<IIconProps, IIconStyles>()({ root: { position: 'relative', top: '2px' } });

export const TranslationConfigurationPage = observer((props: TranslationConfigurationPageProps) => {
    const {
        onNext,
        onSubmit,
        onDismiss,
        onPrevious,
        activeStep,
        saveChanges,
        isSaveDisabled,
        isReturningUser,
        storageAccounts,
        onResourceChange,
        isResourcesLoading,
        translatorResources,
        storageAccountsError,
        selectedResourceStore,
        selectedStorageAccount,
        onStorageAccountChange,
        onRefreshStorageAccounts,
        isStorageAccountsLoading
    } = props;

    const history = useHistory();
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();

    const corsCheckTracker = useProgressTracker();
    const storageAccountPermissionsTracker = useProgressTracker({ persistOnComplete: true });

    const { subscriptionId } = rootStore.azureStore.session;

    const selectedStorageAccountStore = storageAccounts.find(s => s.name === selectedStorageAccount);

    const storageAccountsOptions: IDropdownOption[] = storageAccounts.map(c => ({ key: c.name, text: c.name }));
    const languageResourcesOptions: IDropdownOption[] = translatorResources.map(r => ({ data: r, key: r.id, text: r.name }));

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'save',
            onClick: () => saveChanges(),
            disabled: isSaveDisabled || storageAccountPermissionsTracker.isFailed || storageAccountPermissionsTracker.isRunning,
            iconProps: { iconName: 'Save' },
            text: localize(messages.saveButton)
        }
    ];

    const pathName = history.location.pathname;

    const pageName = React.useMemo(() => getTranslationPageName(pathName), [pathName]);

    React.useEffect(() => {
        if (selectedStorageAccountStore) {
            dispatcher.dispatch('checkStorageAccountRequiredRoles', {
                toastData: {},
                id: subscriptionId,
                tracker: storageAccountPermissionsTracker,
                storageAccount: selectedStorageAccountStore,
                roles: LuisConstants.Azure.StorageBlobDataContributorRoleAssignmentPermissions
            });
            dispatcher.dispatch('checkAndEnableStorageCORS', {
                tracker: corsCheckTracker,
                storageAccountName: selectedStorageAccountStore.name
            });
        }
    }, [selectedStorageAccountStore]);

    return (
        <Stack verticalFill verticalAlign="space-between" styles={{ root: { overflow: 'auto', height: '100%' } }}>
            <ContainerWrapper customProps={{ settingsPage: activeStep === null }}>
                {activeStep !== null && (
                    <Stack tokens={{ childrenGap: 13 }}>
                        <LuisText variant="xLarge" as="h2">
                            {localize(messages.pageTitle)}
                        </LuisText>
                        <LuisText variant="medium">
                            {localize(messages.pageSubtitle)}
                            &nbsp;
                            <Link
                                onClick={() => {
                                    history.push(routeTable.translation.documentTranslationSettings());
                                    logDTLinkClick(
                                        'document translation',
                                        pageName,
                                        'translationSettingClick',
                                        pageName === 'jobCreation' ? getDTActiveStepName(activeStep) : null
                                    );
                                }}>
                                {localize(messages.pageSubtitleLink)}
                            </Link>
                        </LuisText>
                    </Stack>
                )}
                {activeStep === null && <LuisCommandBar items={commandBarItems} />}
                <Stack tokens={{ childrenGap: 9 }} styles={{ root: { paddingTop: '16px' } }}>
                    <Stack tokens={{ childrenGap: 5 }}>
                        <LuisText fontWeight={600} variant="mediumPlus">
                            {localize(messages.translatorResourceTitle)}
                        </LuisText>
                        <LuisText variant="medium" lineHeight="20px">
                            {localize(messages.translatorResourceSubtitle)}
                        </LuisText>
                    </Stack>

                    <Stack tokens={{ childrenGap: 15 }}>
                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                                <LuisDropdown
                                    styles={dropdownStyles}
                                    isLoading={isResourcesLoading}
                                    options={languageResourcesOptions}
                                    selectedKey={selectedResourceStore?.id}
                                    calloutProps={{ styles: calloutStyle }}
                                    data-automation-id="azureResourceDropdown"
                                    onChange={(_, option) => {
                                        onResourceChange(option.key as string);
                                        logDTButtonClick(
                                            'document translation',
                                            pageName,
                                            'dropdown',
                                            'translationResource',
                                            pageName === 'jobCreation' ? getDTActiveStepName(activeStep) : null
                                        );
                                    }}
                                    placeholder={localize(messages.selectTranslatorResourcePlaceholder)}
                                    ariaLabel={`${localize(messages.selectTranslatorResourcePlaceholder)} ${localize(messages.tierNote)}`}
                                    onRenderLabel={() => (
                                        <Label required>
                                            {localize(messages.selectTranslatorResource)} <LuisText>{localize(messages.tierNote)}</LuisText>
                                        </Label>
                                    )}
                                    onRenderOption={p => (
                                        <Stack horizontal verticalFill verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                            <LuisText
                                                color={theme.defaultTextColor}
                                                fontWeight={p.itemType === DropdownMenuItemType.Header && 500}>
                                                {p.text}
                                            </LuisText>
                                            {p.data && (
                                                <SecondaryText variant="xSmall">
                                                    ({(p.data as AzureCsAccountStore)?.region}, {(p.data as AzureCsAccountStore)?.sku.code})
                                                </SecondaryText>
                                            )}
                                        </Stack>
                                    )}
                                />
                            </Stack>

                            {!selectedResourceStore?.isIdentityEnabled && (
                                <CollapsedErrorMessage error={new Error(localize(messages.invalidResource))}>
                                    <Link
                                        onClick={() =>
                                            dispatcher.dispatch('updateActiveGlobalBlade', {
                                                newBlade: 'settings',
                                                settingsTab: 'resources'
                                            })
                                        }>
                                        {localize(messages.enableIdentity)}
                                    </Link>
                                </CollapsedErrorMessage>
                            )}

                            <Stack horizontal verticalAlign="center">
                                <ExternalLink
                                    isUnderlined={false}
                                    styles={createResourceButtonStyles}
                                    onClick={() => {
                                        logDTLinkClick(
                                            'document translation',
                                            pageName,
                                            'createNewResource',
                                            pageName === 'jobCreation' ? getDTActiveStepName(activeStep) : null
                                        );
                                    }}
                                    href="https://aka.ms/createTranlsationResourceAzure">
                                    <Icon iconName="Add" styles={addIconStyles} />
                                    &nbsp;
                                    {localize(messages.createNewResourceLink)}
                                </ExternalLink>
                            </Stack>
                        </Stack>

                        <Stack tokens={{ childrenGap: 9 }}>
                            <Stack tokens={{ childrenGap: 5 }}>
                                <LuisText fontWeight={600} variant="mediumPlus">
                                    {localize(messages.storageAccountSectionTitle)}
                                </LuisText>
                                <LuisText variant="medium" lineHeight="20px">
                                    {localize(messages.storageAccountSectionSubtitle)}
                                </LuisText>
                            </Stack>

                            <Stack tokens={{ childrenGap: 5 }}>
                                <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 5 }}>
                                    <LuisDropdown
                                        required
                                        styles={dropdownStyles}
                                        options={storageAccountsOptions}
                                        selectedKey={selectedStorageAccount}
                                        calloutProps={{ styles: calloutStyle }}
                                        data-automation-id="azureStorageAccountDropdown"
                                        label={localize(messages.selectStorageAccountTitle)}
                                        ariaLabel={localize(messages.selectStorageAccountPlaceholder)}
                                        placeholder={localize(messages.selectStorageAccountPlaceholder)}
                                        onChange={(_, option) => {
                                            onStorageAccountChange(option.key as string);
                                            logDTButtonClick(
                                                'document translation',
                                                pageName,
                                                'dropdown',
                                                'documentStorage',
                                                pageName === 'jobCreation' ? getDTActiveStepName(activeStep) : null
                                            );
                                        }}
                                        isLoading={isStorageAccountsLoading && selectedResourceStore?.isIdentityEnabled}
                                        disabled={!selectedResourceStore?.isIdentityEnabled || !storageAccountsOptions.length}
                                    />
                                    {storageAccountPermissionsTracker.isRunning ||
                                    (isStorageAccountsLoading && selectedResourceStore?.isIdentityEnabled) ? (
                                        <Stack styles={{ root: { height: '32px', width: '32px' } }} verticalAlign="center">
                                            <Spinner />
                                        </Stack>
                                    ) : (
                                        <IconButton
                                            iconProps={{ iconName: 'Refresh', ariaLabel: 'Refresh' }}
                                            onClick={() => onRefreshStorageAccounts()}
                                            title={localize(messages.refreshBtnTitle)}
                                            data-automation-id="storageAccountsRefreshBtn"
                                            ariaLabel={localize(messages.refreshBtnTitle)}
                                        />
                                    )}
                                </Stack>

                                <Stack tokens={{ childrenGap: 5 }}>
                                    {storageAccountPermissionsTracker.isFailed && (
                                        <CollapsedErrorMessage error={new Error(localize(messages.invalidPermissionsStorageAccount))} />
                                    )}

                                    {storageAccountsError && (
                                        <CollapsedErrorMessage
                                            error={storageAccountsError}
                                            collapsedErrorMessage={
                                                storageAccountsError?.name === LuisConstants.HttpResponseCodes.NotAuthorizedAction &&
                                                localize(messages.invalidPermissionsStorageAccount)
                                            }
                                        />
                                    )}

                                    {corsCheckTracker.isFailed && (
                                        <CollapsedErrorMessage
                                            error={corsCheckTracker.error}
                                            collapsedErrorMessage={
                                                corsCheckTracker.error?.name === LuisConstants.HttpResponseCodes.NotAuthorizedAction &&
                                                localize(messages.enableCORSFailed, {
                                                    storageAccountName: selectedStorageAccountStore.name
                                                })
                                            }
                                        />
                                    )}

                                    {!storageAccountsOptions.length && storageAccountPermissionsTracker.isCompleted && (
                                        <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { maxWidth: '500px' } }}>
                                            {localize(messages.emptyStorageAccount)}
                                        </MessageBar>
                                    )}

                                    <ExternalLink
                                        styles={createResourceButtonStyles}
                                        onClick={() => {
                                            logDTLinkClick(
                                                'document translation',
                                                pageName,
                                                'noBlobStore',
                                                pageName === 'jobCreation' ? getDTActiveStepName(activeStep) : null
                                            );
                                        }}
                                        href="https://aka.ms/createStorageResourceAzure">
                                        {localize(messages.createBlobStorageAccountLink)}
                                    </ExternalLink>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            </ContainerWrapper>
            {activeStep !== null && (
                <TranslationToolFooter
                    isDiscardDisabled
                    activeStep={activeStep}
                    isNextDisabled={
                        !selectedStorageAccount ||
                        !selectedResourceStore?.id ||
                        storageAccountPermissionsTracker.isRunning ||
                        storageAccountPermissionsTracker.isFailed ||
                        corsCheckTracker.isFailed
                    }
                    onDismiss={onDismiss}
                    onNext={() => onNext(selectedStorageAccount)}
                    onPrevious={onPrevious}
                    onSubmit={onSubmit}
                    isReturningUser={isReturningUser}
                />
            )}
        </Stack>
    );
});
